import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { toast } from "sonner";
import { CheckCircle2, ShieldAlert } from "lucide-react";
import { completeCfoResearch } from "@/api/cfoWork";
import type {
  CfoAction,
  CfoActionAnswerRead,
  CfoQueueCounts,
  CfoWorkCompleteRequest,
  CfoWorkItem,
} from "@/api/types";
import { Button } from "@/components/ui/button";
import { ReservationWindow } from "@/components/enrichment/ReservationWindow";
import { SessionBatchList } from "@/components/enrichment/SessionBatchList";
import { SessionExpiryBanner } from "@/components/enrichment/SessionExpiryBanner";
import { useCfoResearch, useCfoResearchCache } from "@/hooks/useCfoResearch";
import { useEnrichmentSession } from "@/hooks/useEnrichmentSession";
import { usePermissions } from "@/hooks/usePermissions";
import { inputLabel } from "@/lib/cfoLabels";
import { CfoResearchCard } from "./CfoResearchCard";
import { CfoWorkSetup } from "./CfoWorkSetup";

type Outcome = "answered" | "not_found" | "skipped";

const CLOSE_ERRORS: Record<string, string> = {
  CFO_RESEARCH_ANSWERS_REQUIRED: "Answer at least one action before closing as done.",
  CFO_RESEARCH_ANSWER_NOT_FOUND: "One of the answers is not yours on this company.",
};

function closeError(error: unknown): string {
  if (isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === "string") return CLOSE_ERRORS[detail] ?? detail;
    if (error.response?.status === 403) return "You are not allowed to close CFO research.";
  }
  return "Couldn't close this company. Your answers are saved; try again.";
}

/** The CFO research workstation: a reserved lot, one company at a time.
 *
 *  Same session mechanics as the M&A Work queue (useEnrichmentSession):
 *  closing a company releases it server-side; Skip is the one release the
 *  frontend makes; ending the session hands back the rest. */
export function CfoEnrichmentWork({
  slug,
  active,
  counts,
  onQueueChanged,
}: {
  slug: string;
  active: boolean;
  counts: CfoQueueCounts | null;
  onQueueChanged: () => void;
}) {
  const perms = usePermissions();
  const canResearch = perms.canViewScore && perms.canViewAll;
  // The backend accepts either right for CFO answers and founder reviews.
  const canAnswer = perms.canEdit || perms.canWorkEnrichment;
  const { session, isOpening, openError, isEnding, open, end, skip } = useEnrichmentSession(
    slug,
    "cfo",
    onQueueChanged,
  );
  const items = useMemo<CfoWorkItem[]>(() => session?.cfo_items ?? [], [session]);
  const [index, setIndex] = useState(0);
  const [outcomes, setOutcomes] = useState<Record<number, Outcome>>({});
  const [answers, setAnswers] = useState<string[]>([]);
  const [founders, setFounders] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [cantFindOpen, setCantFindOpen] = useState(false);
  const busyRef = useRef(false);
  const startedAt = useRef(Date.now());
  const pending = useRef<{ signature: string; request: CfoWorkCompleteRequest } | null>(null);
  const cache = useCfoResearchCache(slug);
  const current = index < items.length ? items[index] : undefined;
  const research = useCfoResearch(cache, current?.id ?? null);

  // A new lot starts at its first company.
  useEffect(() => {
    setIndex(0);
    setOutcomes({});
  }, [session]);

  // A new company: its own answers, its own clock.
  useEffect(() => {
    setAnswers([]);
    setFounders([]);
    setCantFindOpen(false);
    startedAt.current = Date.now();
    pending.current = null;
  }, [current?.id]);

  // The next sheet is read while this one is worked.
  useEffect(() => {
    const next = items[index + 1];
    if (next) cache.prefetch(next.id);
  }, [cache, items, index]);

  const advance = useCallback(() => setIndex((i) => i + 1), []);

  const close = useCallback(
    async (asked: "answered" | "not_found", note?: string) => {
      if (!current || busyRef.current) return;
      // Something was answered: the rest not found is a note on an answered company.
      // Its evaluation changes with the answers, so it comes back if still blocked.
      const worked = answers.length + founders.length > 0;
      const result = asked === "not_found" && worked ? "answered" : asked;
      const body = {
        result,
        note: note?.trim() || null,
        answer_ids: result === "answered" ? answers : [],
        founder_ids: result === "answered" ? founders : [],
      };
      const signature = JSON.stringify({ id: current.id, ...body });
      // A retry of the same close keeps its request id: recorded once.
      if (pending.current?.signature !== signature) {
        pending.current = {
          signature,
          request: {
            ...body,
            request_id: crypto.randomUUID(),
            time_spent_seconds: Math.min(
              86_400,
              Math.max(1, Math.round((Date.now() - startedAt.current) / 1000)),
            ),
          },
        };
      }
      busyRef.current = true;
      setBusy(true);
      try {
        await completeCfoResearch(slug, current.id, pending.current.request);
        setOutcomes((all) => ({ ...all, [current.id]: result }));
        advance();
        onQueueChanged();
      } catch (error) {
        toast.error(closeError(error));
      } finally {
        busyRef.current = false;
        setBusy(false);
      }
    },
    [advance, answers, founders, current, onQueueChanged, slug],
  );

  const handleSkip = useCallback(() => {
    if (!current || busyRef.current) return;
    void skip(current.id);
    setOutcomes((all) => ({ ...all, [current.id]: "skipped" }));
    advance();
  }, [advance, current, skip]);

  const onAnswered = useCallback(
    (result: CfoActionAnswerRead, _action: CfoAction) => {
      setAnswers((ids) => (ids.includes(result.observation_id) ? ids : [...ids, result.observation_id]));
    },
    [],
  );

  const onFounderConfirmed = useCallback((founderId: string) => {
    setFounders((ids) => (ids.includes(founderId) ? ids : [...ids, founderId]));
  }, []);

  // S skips, X opens "Can't find": never while typing in a field.
  useEffect(() => {
    if (!active || !current) return;
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        target?.isContentEditable ||
        e.metaKey ||
        e.ctrlKey ||
        e.altKey
      ) {
        return;
      }
      if (e.key === "s" || e.key === "S") handleSkip();
      else if (e.key === "x" || e.key === "X") setCantFindOpen(true);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [active, current, handleSkip]);

  const batch = useMemo(
    () =>
      items.map((item) => ({
        key: item.id,
        primary: item.name,
        secondary:
          outcomes[item.id] === "answered"
            ? "Answered"
            : outcomes[item.id] === "not_found"
              ? "Nothing found"
              : outcomes[item.id] === "skipped"
                ? "Skipped"
                : item.flag === "review"
                  ? "A founder place decides"
                  : item.missing.length
                    ? item.missing.slice(0, 3).map(inputLabel).join(", ")
                    : "Missing data",
      })),
    [items, outcomes],
  );

  if (!canResearch) {
    return (
      <div className="mx-auto flex max-w-lg flex-col items-center gap-3 rounded-plane bg-white p-10 text-center shadow-plane">
        <ShieldAlert className="h-8 w-8 text-slate-400" />
        <h2 className="text-lg font-semibold text-slate-900">CFO research is not open to your role</h2>
        <p className="text-sm text-slate-500">
          It shows what a CFO decision waits for and the founders&rsquo; data: it needs the rights
          to see scores and every company.
        </p>
      </div>
    );
  }

  if (!session) {
    return (
      <CfoWorkSetup
        counts={counts}
        isOpening={isOpening}
        error={openError}
        onStart={(n) => void open(n)}
        onRefresh={onQueueChanged}
      />
    );
  }

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-lg py-20 text-center">
        <h2 className="text-xl font-bold text-slate-900">Nothing to research</h2>
        <p className="mt-2 text-slate-500">
          {session.queue_remaining > 0
            ? `Nothing was free just now: the ${session.queue_remaining} compan${session.queue_remaining === 1 ? "y" : "ies"} left are held by other operators.`
            : "The CFO research queue is empty."}
        </p>
        <Button onClick={() => void end()} className="mt-6" disabled={isEnding}>
          Back to setup
        </Button>
      </div>
    );
  }

  if (!current) {
    const values = Object.values(outcomes);
    const count = (outcome: Outcome) => values.filter((v) => v === outcome).length;
    return (
      <div className="mx-auto max-w-lg py-20 text-center">
        <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-emerald-500" />
        <h2 className="text-xl font-bold text-slate-900">Session complete</h2>
        <p className="mt-2 text-slate-500">
          {items.length} compan{items.length === 1 ? "y" : "ies"}: {count("answered")} answered,{" "}
          {count("not_found")} with nothing found, {count("skipped")} skipped.
        </p>
        <p className="mt-1 text-sm text-slate-400">
          {session.queue_remaining} still waiting in the queue.
        </p>
        <Button onClick={() => void end()} className="mt-6" disabled={isEnding}>
          End session
        </Button>
      </div>
    );
  }

  const answered = Object.values(outcomes).filter((v) => v === "answered").length;

  return (
    <div className="mx-auto max-w-[1400px] space-y-4">
      <SessionExpiryBanner expiresAt={session.expires_at} />
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-slate-500">
            {index + 1} / {items.length} in batch
          </span>
          <span className="text-xs text-slate-400">{session.queue_remaining} left in queue</span>
          <ReservationWindow expiresAt={session.expires_at} />
        </div>
        <div className="flex items-center gap-3">
          <span className="font-medium text-indigo-600">Answered: {answered}</span>
          <button
            type="button"
            onClick={() => void end()}
            disabled={isEnding}
            className="text-xs text-slate-400 hover:text-rose-600 disabled:opacity-50"
            title="Release the companies you have not worked yet"
          >
            End session
          </button>
        </div>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full bg-indigo-500 transition-all duration-300"
          style={{ width: `${(index / items.length) * 100}%` }}
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="min-w-0">
          <CfoResearchCard
            key={current.id}
            slug={slug}
            item={current}
            research={research}
            canAnswer={canAnswer}
            answers={answers.length + founders.length}
            busy={busy}
            cantFindOpen={cantFindOpen}
            onCantFindOpen={setCantFindOpen}
            onAnswered={onAnswered}
            onFounderConfirmed={onFounderConfirmed}
            onRescored={research.reload}
            onDone={() => void close("answered")}
            onSkip={handleSkip}
            onCantFind={(note) => void close("not_found", note)}
          />
        </div>
        <div className="hidden xl:block">
          <div className="sticky top-4">
            <SessionBatchList
              items={batch}
              currentIndex={index}
              processed={index}
              total={items.length}
              accent="indigo"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
